import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFetch, http } from "@/lib/api";
import {
  PageHeader, Loading, ScoreChip, ScoreBar, ScoreBreakdownPopover, Copyable,
  StatCard, SectionTitle, EvidenceBox, riskLabel,
} from "@/components/common";
import AIResearch from "@/components/AIResearch";
import { fmtUsd, fmtNum, fmtPct, timeAgo, riskClass } from "@/lib/format";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import {
  Star, Twitter, Send, Globe, Users, Hammer, ShieldCheck, ShieldAlert,
  AlertTriangle, ArrowLeft,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const TF = ["1m", "5m", "15m", "1h", "4h", "1d"];

export default function TokenResearch() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data, loading } = useFetch(`/tokens/${id}`, [id]);
  const [tf, setTf] = useState("1h");

  if (loading || !data) return <Loading />;
  const t = data.data;

  const addWatch = async () => {
    await http.post("/watchlists", { kind: "token", ref_id: t.id, label: `$${t.symbol}`, sub: t.name });
    toast.success(`$${t.symbol} added to watchlist`);
  };

  const chartData = t.snapshots.map((s) => ({
    t: s.t, price: s.price, mc: s.mc, volume: s.volume, liquidity: s.liquidity,
    net: s.buys - s.sells,
  }));

  return (
    <div className="fade-in">
      <button onClick={() => nav(-1)} className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-amber-400">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>

      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-center gap-3">
          <img src={t.logo} alt="" className="h-12 w-12 rounded-full border border-slate-700 object-cover" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl font-bold text-slate-50">${t.symbol}</h1>
              <span className="text-sm text-slate-400">{t.name}</span>
              <span className="rounded border border-slate-700 bg-slate-800/50 px-1.5 py-0.5 text-[10px] text-slate-300">{t.launch_platform}</span>
            </div>
            <div className="mt-1 flex items-center gap-3">
              <Copyable value={t.address} display={t.short} testid="token-address-copy" />
              <span className="text-xs text-slate-500">Age {timeAgo(t.age_minutes)}</span>
              <div className="flex items-center gap-1.5 text-slate-500">
                {t.socials.twitter && <Twitter className="h-3.5 w-3.5" />}
                {t.socials.telegram && <Send className="h-3.5 w-3.5" />}
                {t.socials.website && <Globe className="h-3.5 w-3.5" />}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ScoreBreakdownPopover type="momentum" score={t.momentum_score} breakdown={t.momentum_breakdown} confidence={t.momentum_confidence}>
            <button><ScoreChip type="momentum" value={t.momentum_score} testid="score-momentum-chip" /></button>
          </ScoreBreakdownPopover>
          <ScoreChip type="organic" value={t.organic_score} confidence={t.organic_confidence} testid="score-organic-chip" />
          <ScoreBreakdownPopover type="risk" score={t.risk_score} breakdown={t.risk_breakdown} confidence={t.risk_confidence}>
            <button><ScoreChip type="risk" value={t.risk_score} testid="score-risk-chip" /></button>
          </ScoreBreakdownPopover>
          <ScoreChip type="confidence" value={t.risk_confidence} testid="score-confidence-chip" />
          <button data-testid="token-watch-btn" onClick={addWatch} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-600/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-300 hover:bg-amber-500/20">
            <Star className="h-3.5 w-3.5" /> Watch
          </button>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <StatCard label="Market Cap" value={fmtUsd(t.market_cap)} />
        <StatCard label="FDV" value={fmtUsd(t.fdv)} />
        <StatCard label="Liquidity" value={fmtUsd(t.liquidity)} sub={`L/MC ${t.liq_mc_ratio}`} />
        <StatCard label="Volume 1h" value={fmtUsd(t.vol_1h)} />
        <StatCard label="Holders" value={fmtNum(t.holders)} sub={fmtPct(t.holder_growth_pct)} tone={t.holder_growth_pct >= 0 ? "pos" : "neg"} />
        <StatCard label="Price 1h" value={fmtPct(t.price_change_1h)} tone={t.price_change_1h >= 0 ? "pos" : "neg"} />
      </div>

      <Tabs defaultValue="chart">
        <TabsList className="mb-4 flex-wrap bg-panel">
          {["chart", "activity", "holders", "wallets", "developer", "security", "narrative", "ai"].map((v) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="capitalize data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300">
              {v === "ai" ? "AI Research" : v === "wallets" ? "Who's Buying" : v}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="chart">
          <div className="pnut-card p-4">
            <div className="mb-3 flex items-center justify-between">
              <SectionTitle>Price / Market Cap</SectionTitle>
              <div className="flex gap-1">
                {TF.map((x) => (
                  <button key={x} onClick={() => setTf(x)} data-testid={`tf-${x}`}
                    className={`rounded px-2 py-0.5 font-mono text-[10px] ${tf === x ? "bg-amber-500/20 text-amber-300" : "text-slate-500 hover:text-slate-300"}`}>
                    {x}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#e6a15c" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#e6a15c" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="t" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={fmtUsd} width={60} />
                <Tooltip contentStyle={{ background: "#131822", border: "1px solid #334155", borderRadius: 8 }} formatter={(v) => fmtUsd(v)} />
                <Area type="monotone" dataKey="mc" stroke="#e6a15c" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
            <div className="mt-4">
              <SectionTitle>Net Buy/Sell Flow</SectionTitle>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={chartData}>
                  <XAxis dataKey="t" hide />
                  <Tooltip contentStyle={{ background: "#131822", border: "1px solid #334155", borderRadius: 8 }} />
                  <Bar dataKey="net">
                    {chartData.map((d, i) => (
                      <Cell key={i} fill={d.net >= 0 ? "#10b981" : "#ef4444"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <div className="grid gap-3 md:grid-cols-4">
            <StatCard label="Buys" value={fmtNum(t.buys)} tone="pos" />
            <StatCard label="Sells" value={fmtNum(t.sells)} tone="neg" />
            <StatCard label="Unique Buyers" value={fmtNum(t.unique_buyers)} />
            <StatCard label="Unique Sellers" value={fmtNum(t.unique_sellers)} />
            <StatCard label="Vol 1m" value={fmtUsd(t.vol_1m)} />
            <StatCard label="Vol 5m" value={fmtUsd(t.vol_5m)} />
            <StatCard label="Vol 15m" value={fmtUsd(t.vol_15m)} />
            <StatCard label="Holder Growth" value={fmtPct(t.holder_growth_pct)} tone={t.holder_growth_pct >= 0 ? "pos" : "neg"} />
          </div>
          <div className="pnut-card mt-4 p-4">
            <SectionTitle>Volume by window</SectionTitle>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={[
                { w: "1m", v: t.vol_1m }, { w: "5m", v: t.vol_5m },
                { w: "15m", v: t.vol_15m }, { w: "1h", v: t.vol_1h },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="w" tick={{ fill: "#64748b", fontSize: 11 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={fmtUsd} width={55} />
                <Tooltip contentStyle={{ background: "#131822", border: "1px solid #334155", borderRadius: 8 }} formatter={(v) => fmtUsd(v)} />
                <Bar dataKey="v" fill="#e6a15c" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        <TabsContent value="holders">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="pnut-card p-4">
              <SectionTitle>Concentration</SectionTitle>
              <div className="text-center">
                <div className="font-display text-4xl font-bold text-slate-50">{t.security.top10_pct}%</div>
                <div className="text-xs text-slate-500">held by top 10 wallets</div>
              </div>
              <p className="mt-3 text-[11px] text-slate-500">
                Higher concentration means fewer wallets can move the market. We do NOT label anyone an
                "insider" merely for buying early.
              </p>
            </div>
            <div className="pnut-card p-4 lg:col-span-2">
              <SectionTitle>Top Holders</SectionTitle>
              <div className="max-h-72 overflow-auto">
                <table className="pnut-table w-full">
                  <thead><tr><th>Wallet</th><th>Label</th><th>Share</th><th>Flags</th></tr></thead>
                  <tbody>
                    {t.holders_list.map((h, i) => (
                      <tr key={i} onClick={() => nav(`/wallet/${h.address}`)}>
                        <td className="font-mono text-slate-300">{h.short}</td>
                        <td className="text-slate-400">{h.label}</td>
                        <td className="font-mono text-slate-200">{h.pct}%</td>
                        <td className="space-x-1">
                          {h.is_dev && <Tag c="rose">DEV</Tag>}
                          {h.is_sniper && <Tag c="orange">SNIPER</Tag>}
                          {h.is_smart && <Tag c="amber">SMART</Tag>}
                          {h.is_tracked && <Tag c="cyan">TRACKED</Tag>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="wallets">
          <div className="pnut-card p-4">
            <SectionTitle right={<span className="inline-flex items-center gap-1 text-xs text-amber-400"><Users className="h-3.5 w-3.5" />{t.tracked_wallets_count} tracked</span>}>
              Who Is Buying?
            </SectionTitle>
            {t.wallet_activity.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">No tracked wallet activity detected for this token yet.</p>
            ) : (
              <div className="max-h-96 overflow-auto">
                <table className="pnut-table w-full">
                  <thead><tr><th>Wallet</th><th>Category</th><th>Action</th><th>Entry MC</th><th>Amount</th><th>Smart</th><th>When</th></tr></thead>
                  <tbody>
                    {t.wallet_activity.map((w, i) => (
                      <tr key={i} onClick={() => nav(`/wallet/${w.address}`)}>
                        <td className="font-mono text-slate-300">{w.short}</td>
                        <td className="text-slate-400">{w.label}</td>
                        <td className={w.action === "BUY" ? "num-pos font-semibold" : "num-neg font-semibold"}>{w.action}</td>
                        <td className="font-mono text-slate-300">{fmtUsd(w.entry_mc)}</td>
                        <td className="font-mono text-slate-400">{w.amount_sol} SOL</td>
                        <td><ScoreChip type="momentum" value={w.smart_score} /></td>
                        <td className="text-slate-500">{timeAgo(w.time_min_ago)} ago</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="developer">
          <DevSection devAddress={t.dev_address} devShort={t.dev_short} devStatus={t.dev_status} devAlloc={t.dev_allocation_pct} nav={nav} />
        </TabsContent>

        <TabsContent value="security">
          <SecuritySection t={t} />
        </TabsContent>

        <TabsContent value="narrative">
          <div className="pnut-card p-5">
            <SectionTitle>Narrative Analysis</SectionTitle>
            <div className="flex items-center gap-2">
              <span className="rounded-lg border border-amber-600/40 bg-amber-500/10 px-3 py-1.5 font-display font-semibold text-amber-300">{t.narrative}</span>
              <button onClick={() => nav("/narrative-radar")} className="text-xs text-slate-500 hover:text-amber-400">View narrative radar →</button>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              This token appears connected to the <span className="text-slate-200">{t.narrative}</span> narrative
              based on its metadata and activity clustering. Narrative association is a signal, not proof of
              legitimacy — always cross-check on-chain evidence.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="ai">
          <AIResearch
            kind="token"
            refId={t.id}
            presets={[
              "Why is this token getting attention?",
              "Is the activity broad or concentrated?",
              "What are the biggest risks?",
              "Are tracked wallets involved?",
            ]}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Tag({ c, children }) {
  const map = {
    rose: "text-rose-400 border-rose-800/50 bg-rose-950/40",
    orange: "text-orange-400 border-orange-800/50 bg-orange-950/40",
    amber: "text-amber-400 border-amber-800/50 bg-amber-950/40",
    cyan: "text-cyan-400 border-cyan-800/50 bg-cyan-950/40",
  };
  return <span className={`rounded border px-1 py-0.5 font-mono text-[9px] font-bold ${map[c]}`}>{children}</span>;
}

function DevSection({ devAddress, devShort, devStatus, devAlloc, nav }) {
  const { data } = useFetch(`/developers/${devAddress}`, [devAddress]);
  if (!data) return <Loading label="Loading developer…" />;
  const d = data.data;
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="pnut-card p-5">
        <SectionTitle right={<Hammer className="h-4 w-4 text-slate-500" />}>Developer</SectionTitle>
        <Copyable value={d.address} display={d.short} />
        <div className="mt-3 space-y-1.5 text-xs">
          <Row k="Status" v={<span className={devStatus === "Selling" ? "text-rose-400" : "text-emerald-400"}>{devStatus}</span>} />
          <Row k="Allocation" v={`${devAlloc}%`} />
          <Row k="Funding source" v={d.funding_source} />
          <Row k="Data quality" v={d.data_quality} />
        </div>
        <button onClick={() => nav(`/developer/${d.address}`)} className="mt-4 w-full rounded-lg border border-slate-700 bg-void py-2 text-xs font-semibold text-slate-300 hover:border-amber-600/40 hover:text-amber-300">
          Open full developer profile →
        </button>
      </div>
      <div className="pnut-card p-5 lg:col-span-2">
        <SectionTitle>Previous Launches ({d.launches_count} analyzable)</SectionTitle>
        <div className="mb-3 grid grid-cols-3 gap-2 text-center">
          <Mini label="$500K+" v={d.reached_500k} />
          <Mini label="$1M+" v={d.reached_1m} />
          <Mini label="$5M+" v={d.reached_5m} />
        </div>
        <div className="max-h-60 overflow-auto">
          <table className="pnut-table w-full">
            <thead><tr><th>Token</th><th>Init MC</th><th>Peak MC</th><th>Survival</th></tr></thead>
            <tbody>
              {d.launches.map((l, i) => (
                <tr key={i} onClick={() => l.token_id && nav(`/token/${l.token_id}`)}>
                  <td className="font-semibold text-slate-200">${l.symbol}</td>
                  <td className="font-mono text-slate-400">{fmtUsd(l.initial_mc)}</td>
                  <td className="font-mono text-slate-200">{fmtUsd(l.peak_mc)}</td>
                  <td className={l.survival === "Rugged" ? "text-rose-400" : l.survival === "Alive" ? "text-emerald-400" : "text-slate-400"}>{l.survival}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SecuritySection({ t }) {
  const s = t.security;
  return (
    <div className="space-y-4">
      {t.providers_disagree && (
        <div className="flex items-center gap-2 rounded-lg border border-orange-700/50 bg-orange-950/30 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-orange-400" />
          <span className="font-display text-sm font-bold text-orange-300">SECURITY PROVIDERS DISAGREE</span>
          <span className="text-xs text-slate-400">— treat the risk score with extra caution and verify manually.</span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <EvidenceBox variant="interesting" title="Why this is interesting" items={t.why_interesting} />
        <EvidenceBox variant="careful" title="Why you should be careful" items={t.why_careful} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="pnut-card p-4 lg:col-span-2">
          <SectionTitle>Risk Breakdown <span className={`ml-2 rounded px-2 py-0.5 font-mono text-xs ${riskClass(t.risk_score)}`}>{riskLabel(t.risk_score)} · {t.risk_score}</span></SectionTitle>
          <div className="grid gap-3 sm:grid-cols-2">
            {Object.entries(t.risk_breakdown).map(([k, v]) => (
              <ScoreBar key={k} label={k + " risk"} value={v} tone="rose" />
            ))}
          </div>
          <div className="mt-3 rounded-md border border-slate-800 bg-void/50 px-3 py-2 text-xs text-slate-400">
            Data confidence for security: <span className="font-mono font-semibold text-cyan-400">{t.risk_confidence}%</span>. Risk and data confidence are kept separate.
          </div>
        </div>

        <div className="pnut-card p-4">
          <SectionTitle>Providers (shown separately)</SectionTitle>
          <div className="space-y-2">
            {t.risk_providers.map((p) => (
              <div key={p.name} className="flex items-center justify-between rounded-md border border-slate-800 bg-void/50 px-3 py-2">
                <div>
                  <div className="text-xs font-semibold text-slate-200">{p.name}</div>
                  <div className="text-[10px] text-slate-500">{p.verdict}</div>
                </div>
                <span className={`rounded px-2 py-0.5 font-mono text-xs ${riskClass(p.score)}`}>{p.score}</span>
              </div>
            ))}
            <p className="text-[10px] text-slate-600">Providers are never blindly averaged.</p>
          </div>
        </div>
      </div>

      <div className="pnut-card p-4">
        <SectionTitle right={<ShieldCheck className="h-4 w-4 text-slate-500" />}>Contract & Liquidity</SectionTitle>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SecItem k="Mint authority" v={s.mint_authority} ok={s.mint_authority === "Revoked"} />
          <SecItem k="Freeze authority" v={s.freeze_authority} ok={s.freeze_authority === "Revoked"} />
          <SecItem k="Update authority" v={s.update_authority} ok={s.update_authority === "Revoked"} />
          <SecItem k="Token program" v={s.token_extensions} ok={s.token_extensions === "Standard SPL"} />
          <SecItem k="LP locked" v={s.lp_locked ? "Yes" : "No"} ok={s.lp_locked} />
          <SecItem k="LP burned" v={`${s.lp_burned_pct}%`} ok={s.lp_burned_pct >= 50} />
          <SecItem k="Top 10 holders" v={`${s.top10_pct}%`} ok={s.top10_pct < 40} />
          <SecItem k="Trading" v={s.trading_restrictions} ok={s.trading_restrictions === "None detected"} />
        </div>
      </div>
    </div>
  );
}

function SecItem({ k, v, ok }) {
  return (
    <div className="rounded-md border border-slate-800 bg-void/50 px-3 py-2">
      <div className="flex items-center gap-1.5">
        {ok ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> : <ShieldAlert className="h-3.5 w-3.5 text-rose-400" />}
        <span className="font-mono text-[10px] uppercase text-slate-500">{k}</span>
      </div>
      <div className={`mt-1 text-sm font-medium ${ok ? "text-emerald-300" : "text-rose-300"}`}>{v}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{k}</span>
      <span className="font-medium text-slate-200">{v}</span>
    </div>
  );
}

function Mini({ label, v }) {
  return (
    <div className="rounded-md border border-slate-800 bg-void/50 py-2">
      <div className="font-display text-xl font-bold text-slate-50">{v}</div>
      <div className="font-mono text-[10px] text-slate-500">{label}</div>
    </div>
  );
}
